import { useState, useEffect } from 'react';

const cache = new Map();

function cleanDescription(html) {
  if (!html) return null;
  return html
    .replace(/<br\s*\/?>/gi, '\n\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'")
    // Open Library descriptions are markdown-ish: strip link syntax and rule lines
    .replace(/\[([^\]]+)\]\((?:https?:)?[^)]*\)/g, '$1')
    .replace(/^\s*[-*_]{3,}\s*$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim() || null;
}

function normTitle(t) {
  return (t ?? '').toLowerCase()
    .replace(/^(the |a |an )/, '')
    .replace(/\s*:.*$/, '')
    .replace(/[^a-z0-9\s]/g, '')
    .trim();
}

function titlesMatch(returned, target) {
  const a = normTitle(returned), b = normTitle(target);
  if (!a || !b) return false;
  if (a === b) return true;
  const shorter = a.length <= b.length ? a : b;
  const longer = a.length <= b.length ? b : a;
  return longer.includes(shorter) && shorter.length / longer.length >= 0.70;
}

async function fetchGoogleBooksDetails(title, author) {
  const key = process.env.REACT_APP_GOOGLE_BOOKS_KEY;
  const q = encodeURIComponent(`intitle:${title} inauthor:${author}`);
  const url = `https://www.googleapis.com/books/v1/volumes?q=${q}&maxResults=1${key ? `&key=${key}` : ''}`;
  const res = await fetch(url);
  if (!res.ok) return null;
  const data = await res.json();
  const item = data.items?.[0];
  if (!item) return null;
  const info = item.volumeInfo;
  return {
    description: cleanDescription(info.description),
    pageCount: info.pageCount ?? null,
    // zoom=2 gives a larger, better-quality thumbnail
    coverUrl: info.imageLinks?.thumbnail
      ? info.imageLinks.thumbnail.replace('http:', 'https:').replace('zoom=1', 'zoom=2')
      : null,
    isbn: info.industryIdentifiers?.find(i => i.type === 'ISBN_13')?.identifier ?? null,
    publisher: info.publisher ?? null,
    categories: info.categories ?? [],
  };
}

// Google Books is rate-limited (429/503) and thin on obscure/translated/small-press
// titles — exactly what this app surfaces. Open Library work records carry a
// description we can fall back to when Google Books gives us nothing.
async function fetchOLDescription(title, author) {
  try {
    const params = new URLSearchParams({ title, author, limit: '3', fields: 'title,author_name,key' });
    const res = await fetch(`https://openlibrary.org/search.json?${params}`);
    if (!res.ok) return null;
    const data = await res.json();
    for (const doc of data.docs ?? []) {
      if (!doc.key || !titlesMatch(doc.title, title)) continue;
      const workRes = await fetch(`https://openlibrary.org${doc.key}.json`);
      if (!workRes.ok) continue;
      const work = await workRes.json();
      const raw = typeof work.description === 'string' ? work.description : work.description?.value;
      const cleaned = cleanDescription(raw);
      if (cleaned) return cleaned;
    }
    return null;
  } catch {
    return null;
  }
}

export function useBookDetails(book) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!book) return;
    const cacheKey = book.key;

    if (cache.has(cacheKey)) {
      setDetails(cache.get(cacheKey));
      return;
    }

    setLoading(true);
    setDetails(null);

    (async () => {
      const gb = await fetchGoogleBooksDetails(book.title, book.author);
      let result = gb;
      // Fall back to Open Library for the description when Google Books has none
      // (missing item, missing description field, or a transient 429/503).
      if (!gb?.description) {
        const olDescription = await fetchOLDescription(book.title, book.author);
        if (olDescription) result = { ...(gb ?? {}), description: olDescription };
      }
      cache.set(cacheKey, result);
      setDetails(result);
    })()
      .catch(() => cache.set(cacheKey, null))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [book?.key]);

  return { details, loading };
}
